import { useState, useEffect } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Shield,
  Users,
  FolderKanban,
  FileText,
  MessageSquare,
  Mail,
  BarChart3,
  Sparkles,
  BookOpen,
  Rocket,
  CheckCircle
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const TUTORIAL_STEPS = [
  {
    id: "welcome",
    icon: Rocket,
    color: "bg-indigo-500",
    title: "Welcome to ClientKit! 🎉",
    subtitle: "Your AI-powered toolkit for freelancer success",
    content: "ClientKit helps you handle difficult client situations, manage projects, create proposals, and much more. Let's take a quick tour of the key features.",
    tip: "You can access this tutorial anytime from the Help menu."
  },
  {
    id: "firewall",
    icon: Shield,
    color: "bg-red-500",
    title: "Client Firewall",
    subtitle: "Handle tough client situations professionally",
    content: "Our AI generates professional, well-crafted replies for tricky situations like late payments, scope creep, or unresponsive clients. Choose your tone (firm, friendly, or formal) and let AI do the heavy lifting.",
    tip: "Try the 'Late Payment Follow-up' template to see it in action!",
    path: "/firewall"
  },
  {
    id: "clients",
    icon: Users,
    color: "bg-blue-500",
    title: "Client Management",
    subtitle: "Keep all your client info organized",
    content: "Store client details, notes, and communication history in one place. The notes field is especially useful for tracking preferences, payment habits, and important context.",
    tip: "Add notes about each client's communication style and payment history.",
    path: "/clients"
  },
  {
    id: "projects",
    icon: FolderKanban,
    color: "bg-indigo-500",
    title: "Project Tracking",
    subtitle: "Manage your work pipeline",
    content: "Track projects by status (pending, active, paused, completed), add tags for easy filtering, and link projects to specific clients. Each project can have proposals, testimonials, and message history.",
    tip: "Use tags to quickly filter projects by type or priority.",
    path: "/projects"
  },
  {
    id: "proposals",
    icon: FileText,
    color: "bg-green-500",
    title: "Proposal Builder",
    subtitle: "Generate professional proposals in seconds",
    content: "Create AI-powered proposals for your projects. Include executive summaries, deliverables, timelines, pricing, and terms. Export as Markdown or share via a client portal link.",
    tip: "Access this from any project page via the Proposals tab."
  },
  {
    id: "testimonials",
    icon: MessageSquare,
    color: "bg-indigo-500",
    title: "Testimonials & Case Studies",
    subtitle: "Build your social proof",
    content: "Generate outreach emails to request testimonials, polish client feedback into quotable testimonials, and create detailed case studies from completed projects.",
    tip: "A polished testimonial can be turned into a case study with one click!"
  },
  {
    id: "templates",
    icon: Mail,
    color: "bg-indigo-500",
    title: "Email Templates",
    subtitle: "Save time on repetitive emails",
    content: "Access a library of 11+ pre-written templates for common scenarios, or create your own custom templates. Use placeholders like {client_name} that auto-fill when you use them.",
    tip: "Create custom templates for emails you send frequently.",
    path: "/email-templates"
  },
  {
    id: "tools",
    icon: Sparkles,
    color: "bg-indigo-500",
    title: "More Powerful Tools",
    subtitle: "SOP Generator, Cold Outreach, Scope Guard",
    content: "Generate Standard Operating Procedures for your business, create personalized cold outreach messages, and define clear scope boundaries and revision policies.",
    tip: "Check out the Analytics dashboard to see your activity overview!",
    path: "/analytics"
  },
  {
    id: "complete",
    icon: CheckCircle,
    color: "bg-indigo-500",
    title: "You're All Set! 🚀",
    subtitle: "Start managing clients like a pro",
    content: "That's the quick tour! Explore the dashboard to see your overview, or jump right into the Client Firewall to generate your first professional reply.",
    tip: "Pro tip: Use the Demo Sign In to see the app with sample data."
  }
];

export default function ProductTutorial({ isOpen, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState(new Set([0]));

  const step = TUTORIAL_STEPS[currentStep];
  const Icon = step.icon;
  const isFirst = currentStep === 0;
  const isLast = currentStep === TUTORIAL_STEPS.length - 1;
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  const handleNext = () => {
    if (isLast) {
      onComplete?.();
      onClose();
    } else {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setVisitedSteps(prev => new Set([...prev, nextStep]));
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete?.();
    onClose();
  };

  const goToStep = (index) => {
    setCurrentStep(index);
    setVisitedSteps(prev => new Set([...prev, index]));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-xl p-0 overflow-hidden">
        {/* Progress Bar */}
        <div className="h-1 bg-slate-800">
          <div 
            className="h-full bg-indigo-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-slate-800 rounded-lg transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 pt-8">
          {/* Step Indicator Dots */}
          <div className="flex justify-center gap-1.5 mb-6">
            {TUTORIAL_STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goToStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep 
                    ? "w-6 bg-indigo-500" 
                    : visitedSteps.has(i)
                      ? "bg-slate-600 hover:bg-slate-400"
                      : "bg-slate-400"
                }`}
                title={s.title}
              />
            ))}
          </div>

          {/* Icon */}
          <div className={`w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <Icon className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-indigo-500 text-center mb-2">
            {step.title}
          </h2>
          <p className="text-indigo-400 text-center font-medium mb-4">
            {step.subtitle}
          </p>

          {/* Content */}
          <p className="text-slate-400 text-center leading-relaxed mb-6">
            {step.content}
          </p>

          {/* Tip Box */}
          {step.tip && (
            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4 mb-6">
              <p className="text-sm text-indigo-400 flex items-start gap-2">
                <BookOpen className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{step.tip}</span>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-700 bg-slate-900/50">
          <div>
            {!isFirst ? (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-3 py-2 text-indigo-400 hover:text-indigo-500 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <button
                onClick={handleSkip}
                className="px-3 py-2 text-slate-400 hover:text-slate-400 transition-colors text-sm"
              >
                Skip tutorial
              </button>
            )}
          </div>
          
          <span className="text-sm text-slate-400">
            {currentStep + 1} / {TUTORIAL_STEPS.length}
          </span>
          
          <button
            onClick={handleNext}
            className="flex items-center gap-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors font-medium"
            data-testid="tutorial-next-btn"
          >
            {isLast ? "Get Started" : "Next"}
            {!isLast && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
